import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { hiddenFromCarrierMap } from 'config/documents';
import { sortBy, isEqual } from 'lodash';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import CheckIcon from 'components/v2/icons/CheckIcon';
import LoadingCircle from 'components/ui/LoadingCircle';
import { CargomaticDropzone } from 'components/v2/Dropzone';
import UploadedDocumentPreviewModal from 'components/modules/shipments/UploadedDocumentPreviewModal';
import { FileSearch, MoreHorizontal, Download, RefreshCw } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import {
  getShipperStopDocumentRequirementsFromConfig,
  getUniqueCombinedStopDocumentRequirementsFromConfig,
} from 'helpers/stops';
import {
  getSignedUrls,
  waitDocumentUpload,
  getUniqueLatestDocumentsV2,
} from 'utils/DocumentsAPIUtils';
import { showToast } from 'stores/ToastStore';
import { shipmentDocumentChanged } from 'utils/ShipmentWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import { useHandleUploadDocuments } from 'utils/DocumentsHooks';
import {
  getAllShipperDocumentRequirements,
  getAllShipperAndCarrierStopDocumentRequirements,
} from 'utils/ShipperRulesAPIUtils';
import { featureEnabled, CONFIGURED_FEATURES } from 'helpers/feature-flags';
import { shouldNotifyServerForMissingDocs } from 'components/modules/documents/Documents';

function Document({ document, onPreview, uploader }) {
  const { t } = useTranslation();
  const { completed } = document;
  function renderStatus() {
    if (completed) {
      return <CheckIcon className="text-green-cm w-5 h-5" />;
    }
    return null;
  }

  function renderActions() {
    if (completed) {
      return (
        <div data-testid="div-2d5db973" className="flex items-center space-x-1">
          <Button variant="ghost" size="icon" onClick={onPreview}>
            <FileSearch className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <a
                  href={document?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  {t('DOWNLOAD')}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                className="gap-2 p-0"
              >
                <RefreshCw className="h-4 w-4 ml-2" />
                {uploader()}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    }

    return uploader(true);
  }
  return (
    <div
      data-testid="div-d5623f65"
      className="flex justify-between text-sm text-gray-700 py-1 items-center"
    >
      <div data-testid="div-ba6a71ba" className="flex items-center space-x-2">
        <span data-testid="span-2828a3eb">{t(sluggify(document.name))}</span>
        {renderStatus()}
      </div>
      {renderActions()}
    </div>
  );
}

function usePreviousProps(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export default function MinimumDocumentRequirements({
  shipment,
  title = 'MINIMUM_DOCUMENT_REQUIREMENTS',
}) {
  const { t } = useTranslation();
  const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(
    CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS,
  );
  const isFromCarrierDashboard =
    title === 'PLEASE_PROVIDE_THE_FOLLOWING_DOCUMENTS';
  const isCarrier = SessionStore.isCarrier();
  const dropzoneRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [stopDocumentsConfig, setStopDocumentsConfig] = useState({});
  const [documents, setDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [documentsUploading, setDocumentsUploading] = useState({});
  const [carrierDocuments, setCarrierDocuments] = useState([]);
  const [shipperDocuments, setShipperDocuments] = useState([]);

  const keys = [
    shipment._id.toString(),
    ...shipment.stops.map((stop) => stop._id.toString()),
  ];

  const previous = usePreviousProps(shipment);
  useEffect(() => {
    if (shipment?.shipper && !isEqual(shipment, previous)) {
      getUploadedDocuments();
      getShipperStopDocumentRequirements();
    }
  }, [shipment]);

  useEffect(() => {
    if (shipment && Object.keys(stopDocumentsConfig).length) {
      getAllDocumentRequirements();
    }
  }, [uploadedDocuments, stopDocumentsConfig]);

  const getShipperStopDocumentRequirements = async () => {
    try {
      setLoading(true);
      const shipperId = shipment?.shipper?._id || shipment?.shipper;
      const config = isCarrierMandatoryStopDocumentsEnabled
        ? await getUniqueCombinedStopDocumentRequirementsFromConfig(
            shipperId,
            false,
          )
        : await getShipperStopDocumentRequirementsFromConfig(shipperId);
      setStopDocumentsConfig(config);
    } catch (e) {
      showToast(
        t('ERROR_RETRIEVING_STOP_DOCUMENT_REQUIREMENTS'),
        null,
        'error',
      );
      console.error(
        `Error retrieving shipper stop documents config: ${e.message}`,
      );
    }
  };

  async function getUploadedDocuments() {
    try {
      const shipperId = shipment?.shipper?._id ?? shipment?.shipper;
      let documentRequirements = [];
      if (isCarrierMandatoryStopDocumentsEnabled) {
        const combinedConfig =
          await getUniqueCombinedStopDocumentRequirementsFromConfig(
            shipperId,
            false,
          );
        const [combinedDocs] =
          await getAllShipperAndCarrierStopDocumentRequirements(
            shipment,
            combinedConfig,
          );
        documentRequirements = combinedDocs;
      } else {
        const config =
          await getShipperStopDocumentRequirementsFromConfig(shipperId);
        documentRequirements = await getAllShipperDocumentRequirements(
          shipment,
          config,
        );
      }

      const getDocumentsResult = await getUniqueLatestDocumentsV2(
        keys,
        documentRequirements,
      );
      const uploadedDocuments =
        getDocumentsResult?.data?.getUniqueLatestDocumentsV2?.documents || [];
      const getSignedUrlsResult = await getSignedUrls(keys);
      const signedUrls =
        getSignedUrlsResult?.data?.getSignedUrls?.signedUrls || [];

      const documents = uploadedDocuments.map((doc) => {
        const url = signedUrls.find((url) => url.id === doc.id);
        return {
          ...doc,
          url: url?.signedUrl,
        };
      });

      setUploadedDocuments(documents);
    } catch (e) {
      console.error(`Error retrieving uploaded documents: ${e.message}`);
    }
  }

  function mapDocumentUploadStatus(
    documentRequirements,
    excludeYardInterchange = false,
  ) {
    const sortedDocuments = sortBy(
      uploadedDocuments,
      (doc) => doc.createdAt,
    ).reverse();
    let docs = documentRequirements;
    if (excludeYardInterchange) {
      const isYardInterchangeUploaded = sortedDocuments.some(
        ({ type }) => type === 'yard_interchange_ticket',
      );
      if (!isYardInterchangeUploaded) {
        docs = documentRequirements.filter(
          (docType) => docType !== 'yard_interchange_ticket',
        );
      }
    }

    const documentsStatus = docs.map((type) => {
      const uploadedDocument = sortedDocuments.find((doc) => doc.type === type);
      return {
        name: type,
        url: uploadedDocument?.url,
        completed: !!uploadedDocument,
      };
    });
    return documentsStatus;
  }

  async function getAllDocumentRequirements() {
    try {
      let allDocuments = [];
      let carrierDocuments = [];
      let shipperDocuments = [];

      if (isCarrierMandatoryStopDocumentsEnabled) {
        const [combinedDocs, carrierStopDocs, allShipperDocs] =
          await getAllShipperAndCarrierStopDocumentRequirements(
            shipment,
            stopDocumentsConfig,
          );
        allDocuments = combinedDocs;
        carrierDocuments = carrierStopDocs;
        shipperDocuments = allShipperDocs;
      } else {
        allDocuments = await getAllShipperDocumentRequirements(
          shipment,
          stopDocumentsConfig,
        );
      }

      if (isCarrier) {
        allDocuments = allDocuments.filter((doc) => !hiddenFromCarrierMap[doc]);
      }
      const shouldCheckToExcludeYardInterchange = carrierDocuments.includes(
        'yard_interchange_ticket',
      );
      const isYardInterchangePresentInShipperAndAccessorialDocs =
        isCarrier &&
        isCarrierMandatoryStopDocumentsEnabled &&
        shipperDocuments?.includes('yard_interchange_ticket');
      const shouldExcludeYardInterchangeForCarrier =
        !isFromCarrierDashboard &&
        !isYardInterchangePresentInShipperAndAccessorialDocs &&
        shouldCheckToExcludeYardInterchange;

      setCarrierDocuments(
        mapDocumentUploadStatus(
          carrierDocuments,
          shouldCheckToExcludeYardInterchange,
        ),
      );
      setShipperDocuments(mapDocumentUploadStatus(shipperDocuments));

      setDocuments(
        mapDocumentUploadStatus(
          allDocuments,
          shouldExcludeYardInterchangeForCarrier,
        ),
      );

      setLoading(false);
    } catch (e) {
      showToast(t('ERROR_RETRIEVING_DOCUMENT_REQUIREMENTS'), null, 'error');
      console.error(`Error retrieving document requirements: ${e.message}`);
    }
  }

  async function onUpload() {
    await getUploadedDocuments();
    if (shouldNotifyServerForMissingDocs(shipment)) {
      shipmentDocumentChanged(shipment._id);
    }
  }

  const { handleUpload } = useHandleUploadDocuments(shipment, null, onUpload);

  const uploadDocument = async (files, docType) => {
    try {
      dropzoneRef?.current?.open();
      let documentResult;
      documentResult = await handleUpload(
        files.map(
          (file) =>
            new File([file], docType, {
              ...file.options,
              type: file.type,
            }),
        ),
        docType,
        undefined,
        true,
      );
      if (documentResult?.data) {
        documentResult = await waitDocumentUpload(documentResult.data.key);
      }
      return documentResult;
    } catch (e) {
      console.log('error in uploadDocument: ', e);
    }
  };

  async function upload(files, documentType) {
    try {
      if (files.length) {
        const documentResult = await uploadDocument(files, documentType);
        return documentResult;
      }
    } catch (e) {
      showToast(t('UPLOAD_FAILED'), null, 'error');
      console.log('error in document upload: ', e);
    }
  }

  const renderRowUploader = (
    document,
    isUploading,
    setDocumentUploading,
    showIcon = false,
  ) => {
    try {
      if (isUploading) {
        return (
          <LoadingCircle
            dataTestId="LoadingCircle-646dd0ef"
            text={t('UPLOADING')}
          />
        );
      }
      return (
        <CargomaticDropzone
          dataTestId="CargomaticDropzone-f889e38f"
          dropzoneRef={dropzoneRef}
          onDrop={async (files) => {
            setDocumentUploading();
            await upload(files, document.name);
            setDocumentUploading();
          }}
          style={{ padding: 0, borderWidth: 0, borderRadius: 0 }}
          multiple={false}
          acceptedFiles={ACCEPTED_FILE_TYPES}
          disabled={isUploading}
          dropzoneMessage={!showIcon ? t('REPLACE') : ''}
          button={showIcon}
          showIcon={showIcon}
        />
      );
    } catch (e) {
      console.error(`Error rendering uploader: ${e.message}`);
    }
  };

  function documentIsUploading(name) {
    setDocumentsUploading((currentDocumentsUploading) => {
      if (currentDocumentsUploading[name]) {
        return {
          ...currentDocumentsUploading,
          [name]: false,
        };
      }
      return {
        ...currentDocumentsUploading,
        [name]: true,
      };
    });
  }

  function renderTitle() {
    return (
      <div data-testid="div-ae083836" className="mb-2">
        <h3 data-testid="h3-1a9b6644" className="text-left">
          {t(title)}
        </h3>
      </div>
    );
  }

  function renderCarrierTitle() {
    return (
      <div data-testid="div-ae083846" className="mb-2">
        <h3 data-testid="h3-1a9b6634" className="text-left">
          {t('CARRIER_MANDATORY_DOCUMENTS')}
        </h3>
      </div>
    );
  }

  function documentUploader(showIcon, documentToUpload) {
    return renderRowUploader(
      documentToUpload,
      documentsUploading[documentToUpload.name],
      () => documentIsUploading(documentToUpload.name),
      showIcon,
    );
  }

  function renderContent(loading, requiredDocuments) {
    if (loading) {
      return (
        <div data-testid="div-a014bb1d">
          <LoadingCircle
            dataTestId="LoadingCircle-9cfc31f6"
            text={t('FETCHING_REQUIREMENTS')}
          />
        </div>
      );
    }

    return (
      <div data-testid="div-a188f0ee">
        {!requiredDocuments.length && (
          <p data-testid="p-8e85c987">{t('NO_DOCUMENTS_REQUIRED')}</p>
        )}
        {requiredDocuments.map((document) => (
          <Document
            document={document}
            key={document.name}
            onPreview={() => setDocumentToPreview(document)}
            uploader={(showIcon) => documentUploader(showIcon, document)}
          />
        ))}
        {documentToPreview && (
          <UploadedDocumentPreviewModal
            name={documentToPreview.name}
            url={documentToPreview.url}
            onHide={() => setDocumentToPreview(null)}
          />
        )}
      </div>
    );
  }
  const showInSeperateSections =
    isCarrierMandatoryStopDocumentsEnabled &&
    !isCarrier &&
    !isFromCarrierDashboard;
  const docs = showInSeperateSections ? shipperDocuments : documents;
  return (
    <div
      data-testid="MinimumDocumentRequirements-8hb231ce"
      className="lg:col-span-4"
    >
      <CollapsibleItem
        dataTestId="CollapsibleItem-9db832fc"
        renderTitle={renderTitle}
        className="mb-4"
        defaultOpen
      >
        {renderContent(loading, docs)}
      </CollapsibleItem>
      {showInSeperateSections && (
        <CollapsibleItem
          dataTestId="CollapsibleItem-9db823fe"
          renderTitle={renderCarrierTitle}
          className="mb-4"
          defaultOpen
        >
          {renderContent(loading, carrierDocuments)}
        </CollapsibleItem>
      )}
    </div>
  );
}
